import { useEffect, useState } from 'react';
import { reportApi } from '../../../../api/reportApi';
import { sessionsApi } from '../../../../api/sessionsApi';
import { sessionReportFields, withStoredReport } from '../utils/storedReportMarkdown';

const TAB_OF_FIELD = {
  session_at_glance_md: 'at_glance',
  full_report_md: 'full_report',
  speakers_md: 'speakers',
  in_depth_md: 'analysis',
};

const TAB_NAMES = {
  at_glance: 'Session at a glance',
  full_report: 'Full report',
  speakers: 'Speakers',
  analysis: 'In-depth analysis',
};

const EMPTY_REPORT = {
  session_at_glance_md: '',
  full_report_md: '',
  speakers_md: '',
  in_depth_md: '',
  additional_analysis: null,
};

const primaryLanguageOf = (session) => String(
  session?.primary_language || session?.primaryLanguage
  || session?.transcript_language || session?.transcriptLanguage || 'en',
).toLowerCase();

/** The session in the requested language: the primary language first, then the translation when one is asked for. */
const loadSessionIn = async (sessionId, requestedLang) => {
  const primary = await sessionsApi.get(sessionId);
  const lang = (requestedLang || primaryLanguageOf(primary)).toLowerCase();
  return lang === primaryLanguageOf(primary) ? primary : sessionsApi.get(sessionId, { lang });
};

const loadStoredReportData = async (sessionId) => {
  try {
    const status = await reportApi.getSessionReportStatus(sessionId);
    if (status.status !== 'COMPLETED') return null;
    return (await reportApi.getSessionReport(sessionId)).data;
  } catch (err) {
    console.warn('Could not load report data, using session data only:', err);
    return null;
  }
};

const savedMessage = (tabs) => (tabs.length === 1 ? `${TAB_NAMES[tabs[0]]} saved.` : 'All report sections saved.');

/**
 * The report sections of a session for the report editor, in the `?lang=` language,
 * and saving edited sections back (to the translation when it is not the primary language).
 */
export function useEditableReport(sessionId, requestedLang, notify) {
  const [session, setSession] = useState(null);
  const [report, setReport] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState({});

  const primaryLanguage = primaryLanguageOf(session);
  const currentLang = (requestedLang || primaryLanguage).toLowerCase();
  const isTranslation = currentLang !== primaryLanguage;

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const loaded = await loadSessionIn(sessionId, requestedLang);
        setSession(loaded);
        setReport(withStoredReport(sessionReportFields(loaded), await loadStoredReportData(sessionId)));
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, requestedLang]);

  const save = async (payload) => {
    const tabs = Object.keys(payload).map((field) => TAB_OF_FIELD[field]).filter(Boolean);
    try {
      setIsSaving(true);
      setSaveErrors({});
      await sessionsApi.update(sessionId, payload, isTranslation ? currentLang : null);
      setReport((prev) => ({ ...prev, ...payload }));
      notify({ type: 'success', title: 'Saved', message: savedMessage(tabs) });
      return true;
    } catch (err) {
      const message = err?.message || 'Failed to save changes';
      setSaveErrors(Object.fromEntries(tabs.map((tab) => [tab, message])));
      notify({ type: 'error', title: 'Save failed', message });
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return { session, report, loading, error, isSaving, saveErrors, currentLang, save };
}
